import { motion } from 'framer-motion';
import { Mail, Github, ExternalLink, MessageSquare, GitPullRequest, Star } from 'lucide-react';

export default function ContactSection() {
  const handleContactClick = () => {
    const subject = encodeURIComponent('AI Periodic Table - Feedback/Contribution');
    const body = encodeURIComponent(`Hi Sankar,

I came across the AI Periodic Table and wanted to reach out about:

[ ] Suggesting a new element
[ ] Reporting an error
[ ] Collaboration opportunity
[ ] General feedback
[ ] Other

Details:


Best regards`);
    
    window.location.href = `mailto:sankarb@iisc.ac.in?subject=${subject}&body=${body}`;
  };

  return (
    <div className="max-w-4xl mx-auto px-4">
      {/* Section Header */}
      <motion.div
        className="text-center mb-12"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
      >
        <h2 className="text-3xl md:text-4xl font-display font-bold mb-4">
          Get <span className="text-gradient">Involved</span>
        </h2>
        <p className="text-gray-400 max-w-2xl mx-auto">
          The AI Periodic Table is an open framework. Contribute, suggest improvements, 
          or use it in your work.
        </p>
      </motion.div>

      {/* Contact Cards */}
      <div className="grid md:grid-cols-2 gap-6 mb-12">
        {/* Email Contact */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="p-6 rounded-xl bg-dark-800/50 border border-white/5 hover:border-cyber-blue/30 transition-colors group"
        >
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-12 rounded-xl bg-cyber-blue/20 flex items-center justify-center group-hover:scale-110 transition-transform">
              <Mail className="w-6 h-6 text-cyber-blue" />
            </div>
            <div>
              <h3 className="font-bold text-lg">Contact</h3>
              <p className="text-sm text-gray-400">Send feedback or suggestions</p>
            </div>
          </div>
          <p className="text-gray-400 text-sm mb-4">
            Have ideas for new elements? Found an error? Want to collaborate? 
            Reach out directly.
          </p>
          <button
            onClick={handleContactClick}
            className="w-full px-4 py-3 rounded-lg bg-cyber-blue/20 hover:bg-cyber-blue/30 text-cyber-blue font-medium transition-colors flex items-center justify-center gap-2"
          >
            <MessageSquare className="w-5 h-5" />
            Send Email
          </button>
        </motion.div>

        {/* GitHub */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
          className="p-6 rounded-xl bg-dark-800/50 border border-white/5 hover:border-cyber-purple/30 transition-colors group"
        >
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-12 rounded-xl bg-cyber-purple/20 flex items-center justify-center group-hover:scale-110 transition-transform">
              <Github className="w-6 h-6 text-cyber-purple" />
            </div>
            <div>
              <h3 className="font-bold text-lg">GitHub Repository</h3>
              <p className="text-sm text-gray-400">Open source contributions</p>
            </div>
          </div>
          <p className="text-gray-400 text-sm mb-4">
            Fork the repository, submit pull requests, or open issues. 
            All contributions welcome!
          </p>
          <a
            href="https://github.com/sankar-mechengg/ai-periodic-table"
            target="_blank"
            rel="noopener noreferrer"
            className="w-full px-4 py-3 rounded-lg bg-cyber-purple/20 hover:bg-cyber-purple/30 text-cyber-purple font-medium transition-colors flex items-center justify-center gap-2"
          >
            <ExternalLink className="w-5 h-5" />
            View on GitHub
          </a>
        </motion.div>
      </div>

      {/* Contribution Ideas */}
      <motion.div
        className="p-6 rounded-xl bg-gradient-to-br from-cyber-blue/10 to-cyber-purple/10 border border-white/5"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
      >
        <h3 className="text-xl font-bold mb-6 text-center">Ways to Contribute</h3>
        <div className="grid sm:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-3">
              <GitPullRequest className="w-6 h-6 text-green-500" />
            </div>
            <h4 className="font-semibold mb-2">Suggest Elements</h4>
            <p className="text-sm text-gray-400">
              Know a technique or concept that should be included? Open an issue.
            </p>
          </div>
          <div className="text-center">
            <div className="w-12 h-12 rounded-full bg-yellow-500/20 flex items-center justify-center mx-auto mb-3">
              <Star className="w-6 h-6 text-yellow-500" />
            </div>
            <h4 className="font-semibold mb-2">Star & Share</h4>
            <p className="text-sm text-gray-400">
              Help others discover the framework by starring and sharing.
            </p>
          </div>
          <div className="text-center">
            <div className="w-12 h-12 rounded-full bg-blue-500/20 flex items-center justify-center mx-auto mb-3">
              <MessageSquare className="w-6 h-6 text-blue-500" />
            </div>
            <h4 className="font-semibold mb-2">Give Feedback</h4>
            <p className="text-sm text-gray-400">
              What works? What doesn't? Your input shapes future versions.
            </p>
          </div>
        </div>
      </motion.div>

      {/* Usage License */}
      <motion.div
        className="mt-8 text-center text-sm text-gray-500"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
      >
        <p>
          The AI Periodic Table is released under{' '}
          <a href="https://creativecommons.org/licenses/by/4.0/" className="text-cyber-blue hover:underline">
            CC BY 4.0
          </a>
          . Use freely with attribution.
        </p>
      </motion.div>
    </div>
  );
}
